import { gql } from "@apollo/client";

export const AVAILABLE_PROVIDERS = gql`
  query AvailableProviders {
    availableProviders
  }
`;

export const SESSION_CONTEXT = gql`
  query SessionContext {
    sessionContext {
      authenticated
      provider
      instanceUrl
      analyticsId
      debugAccess {
        enabled
        authDebug
        ddbCacheDebug
        comparePerformanceDebug
        compareStreamDebug
        designSystem
        developerTools
      }
    }
  }
`;

export const AUTH_DEBUG_CONTEXT = gql`
  query AuthDebugContext {
    authDebugContext {
      authenticated
      dataSource
      headerConnectionId
      sessionProvider
      sessionConnectionId
      resolvedConnectionId
      resolvedProvider
      resolvedInstanceUrl
      userIdPresent
      hasResolvedConnection
    }
  }
`;

export const ME = gql`
  query Me {
    me {
      id
      displayName
      primaryEmail
      avatarUrl
      firstRunDismissedAt
      contactPrefsAcknowledgedAt
      marketingConsentAt
      termsAcceptedAt
      termsAcceptedVersion
      privacyAcknowledgedAt
      privacyAcknowledgedVersion
      verifiedEmails {
        email
        providers
        isPrimary
      }
    }
  }
`;

export const SUBSCRIBE_TO_UPDATES = gql`
  mutation SubscribeToUpdates($email: String!) {
    subscribeToUpdates(email: $email)
  }
`;

export const ACKNOWLEDGE_CONTACT_PREFS = gql`
  mutation AcknowledgeContactPrefs(
    $email: String!
    $marketingOptIn: Boolean!
    $legalAccepted: Boolean!
  ) {
    acknowledgeContactPrefs(
      email: $email
      marketingOptIn: $marketingOptIn
      legalAccepted: $legalAccepted
    ) {
      id
      primaryEmail
      contactPrefsAcknowledgedAt
      marketingConsentAt
      termsAcceptedAt
      termsAcceptedVersion
      privacyAcknowledgedAt
      privacyAcknowledgedVersion
      verifiedEmails {
        email
        providers
        isPrimary
      }
    }
  }
`;

export const DISMISS_FIRST_RUN = gql`
  mutation DismissFirstRun {
    dismissFirstRun {
      id
      firstRunDismissedAt
    }
  }
`;

export const MY_CONNECTIONS = gql`
  query MyConnections {
    myConnections {
      id
      provider
      instanceUrl
      providerUserId
      login
      profileName
      avatarUrl
      createdAt
      updatedAt
      verifiedEmails
      status
      userActionRequired
      manageInstallationUrl
    }
  }
`;

export const UPDATE_DISPLAY_NAME = gql`
  mutation UpdateDisplayName($displayName: String!) {
    updateDisplayName(displayName: $displayName) {
      id
      displayName
      primaryEmail
      avatarUrl
      verifiedEmails {
        email
        providers
        isPrimary
      }
    }
  }
`;

export const SET_PRIMARY_EMAIL = gql`
  mutation SetPrimaryEmail($email: String!) {
    setPrimaryEmail(email: $email) {
      id
      displayName
      primaryEmail
      avatarUrl
      verifiedEmails {
        email
        providers
        isPrimary
      }
    }
  }
`;

export const DISCONNECT_CONNECTION = gql`
  mutation DisconnectConnection($connectionId: ID!) {
    disconnectConnection(connectionId: $connectionId) {
      disconnectedConnectionId
      activeConnectionId
      myConnections {
        id
        provider
        instanceUrl
        providerUserId
        login
        profileName
        avatarUrl
        createdAt
        updatedAt
        verifiedEmails
        status
        userActionRequired
        manageInstallationUrl
      }
    }
  }
`;

export const RECONCILE_CONNECTION = gql`
  mutation ReconcileConnection($connectionId: ID!) {
    reconcileConnection(connectionId: $connectionId) {
      id
      provider
      instanceUrl
      providerUserId
      login
      profileName
      avatarUrl
      createdAt
      updatedAt
      verifiedEmails
      status
      userActionRequired
      manageInstallationUrl
    }
  }
`;

export const DELETE_ACCOUNT = gql`
  mutation DeleteAccount {
    deleteAccount
  }
`;

export const SIGN_OUT = gql`
  mutation SignOut {
    signOut
  }
`;

export const PROJECTS = gql`
  query Projects {
    projects {
      id
      pathWithNamespace
      webUrl
      defaultBranch
      provider
    }
  }
`;

export const PROJECT_BRANCHES = gql`
  query ProjectBranches($projectPath: String!) {
    projectBranches(projectPath: $projectPath)
  }
`;

export const AVAILABLE_REFS = gql`
  query AvailableRefs($projectPath: String!) {
    availableRefs(projectPath: $projectPath) {
      name
      kind
      createdAt
    }
  }
`;

// Analysis-gate cache-aware blast radius: NEW (uncached) records a Run would create.
export const COMPARE_BLAST_RADIUS = gql`
  query CompareBlastRadius($input: CompareQueryInput!) {
    compareBlastRadius(input: $input) {
      refs
      variants
      estimatedNewRecords
      cachedRatio
    }
  }
`;

// Analysis-gate discovery nudge: strong job families on refs outside the current scope.
export const STRONG_FAMILIES_OUTSIDE_SCOPE = gql`
  query StrongFamiliesOutsideScope(
    $projectPath: String!
    $candidatePatterns: [String!]!
    $currentScopeRefs: [String!]!
    $limit: Int
  ) {
    strongFamiliesOutsideScope(
      projectPath: $projectPath
      candidatePatterns: $candidatePatterns
      currentScopeRefs: $currentScopeRefs
      limit: $limit
    ) {
      pattern
      refCount
      family
      score
      sampleRefs
    }
  }
`;

export const PROJECT_MEMORY = gql`
  query ProjectMemory {
    projectMemory {
      provider
      connectionId
      lastProjectPath
      recentProjects {
        projectPath
        displayName
        selectedAt
        lastComparedAt
      }
    }
  }
`;

export const RECORD_PROJECT_SELECTION = gql`
  mutation RecordProjectSelection($projectPath: String!) {
    recordProjectSelection(projectPath: $projectPath) {
      provider
      connectionId
      lastProjectPath
      recentProjects {
        projectPath
        displayName
        selectedAt
        lastComparedAt
      }
    }
  }
`;

export const JOB_PARSER_MAP = gql`
  query JobParserMap($projectPath: String!) {
    jobParserMap(projectPath: $projectPath) {
      job
      parser
      source
    }
  }
`;

export const PROJECT_SOURCE_SELECTION = gql`
  query ProjectSourceSelection($projectPath: String!) {
    projectSourceSelection(projectPath: $projectPath) {
      selectedSource {
        mode
        jobFamily
        parser
      }
    }
  }
`;

export const WEBHOOK_SETTINGS = gql`
  query WebhookSettings($provider: String) {
    webhookSettings(provider: $provider) {
      coverage {
        verified
        total
        failing
      }
      rows {
        provider
        projectPath
        displayPath
        analyzedAt
        hasAnalysisContext
        selectedSource {
          mode
          jobFamily
          parser
        }
        config {
          provider
          projectPath
          status
          createdAt
          updatedAt
          lastReceivedAt
          lastAcceptedAt
          payloadUrl
          events
        }
        notificationDestinations {
          id
          type
          provider
          projectPath
          channel
          channelId
          teamName
          teamId
          configurationUrl
          status
          branches
          allBranches
          jobFamily
          parser
          triggers
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const WEBHOOK_CONFIG_FIELDS = gql`
  fragment WebhookConfigFields on WebhookConfig {
    provider
    projectPath
    status
    createdAt
    updatedAt
    lastReceivedAt
    lastAcceptedAt
    payloadUrl
    events
  }
`;

const SLACK_DESTINATION_FIELDS = gql`
  fragment SlackDestinationFields on SlackDestination {
    id
    type
    provider
    projectPath
    channel
    channelId
    teamName
    teamId
    configurationUrl
    status
    branches
    allBranches
    jobFamily
    parser
    triggers
    createdAt
    updatedAt
  }
`;

export const CREATE_WEBHOOK_CONFIG = gql`
  ${WEBHOOK_CONFIG_FIELDS}
  mutation CreateWebhookConfig($provider: String!, $projectPath: String!) {
    createWebhookConfig(provider: $provider, projectPath: $projectPath) {
      signingToken
      config {
        ...WebhookConfigFields
      }
    }
  }
`;

export const DELETE_WEBHOOK_CONFIG = gql`
  mutation DeleteWebhookConfig($provider: String!, $projectPath: String!) {
    deleteWebhookConfig(provider: $provider, projectPath: $projectPath) {
      ok
    }
  }
`;

export const UPDATE_SLACK_DESTINATION_ROUTING = gql`
  ${SLACK_DESTINATION_FIELDS}
  mutation UpdateSlackDestinationRouting(
    $id: ID!
    $allBranches: Boolean!
    $branches: [String!]
    $jobFamily: String!
    $parser: String!
    $triggers: [String!]!
  ) {
    updateSlackDestinationRouting(
      id: $id
      allBranches: $allBranches
      branches: $branches
      jobFamily: $jobFamily
      parser: $parser
      triggers: $triggers
    ) {
      ...SlackDestinationFields
    }
  }
`;

export const DELETE_SLACK_DESTINATION = gql`
  mutation DeleteSlackDestination($id: ID!) {
    deleteSlackDestination(id: $id) {
      ok
      slackRevokeAttempted
      slackRevokeSucceeded
    }
  }
`;

export const SET_PROJECT_SOURCE = gql`
  mutation SetProjectSource(
    $projectPath: String!
    $mode: String!
    $jobFamily: String
    $parser: String
  ) {
    setProjectSource(
      projectPath: $projectPath
      mode: $mode
      jobFamily: $jobFamily
      parser: $parser
    ) {
      selectedSource {
        mode
        jobFamily
        parser
      }
    }
  }
`;

export const PARSER_CATALOG = gql`
  query ParserCatalog {
    parserCatalog {
      name
      label
      tags
      group
      version
      specYaml
      tool
      language
    }
  }
`;

export const CUSTOM_PARSERS = gql`
  query CustomParsers($projectPath: String!) {
    customParsers(projectPath: $projectPath) {
      name
      projectPath
      specYaml
      updatedAt
    }
  }
`;

export const AUTO_DETECT_JOB_PARSERS = gql`
  mutation AutoDetectJobParsers($projectPath: String!, $limit: Int, $ref: String) {
    autoDetectJobParsers(projectPath: $projectPath, limit: $limit, ref: $ref) {
      job
      parser
      score
    }
  }
`;

export const AUTO_DETECT_JOB_PARSER = gql`
  mutation AutoDetectJobParser(
    $projectPath: String!
    $jobName: String!
    $limit: Int
    $ref: String
  ) {
    autoDetectJobParser(projectPath: $projectPath, jobName: $jobName, limit: $limit, ref: $ref) {
      job
      parser
      score
    }
  }
`;

export const PREVIEW_CUSTOM_PARSER = gql`
  mutation PreviewCustomParser($name: String!, $specYaml: String!, $sampleLog: String!) {
    previewCustomParser(name: $name, specYaml: $specYaml, sampleLog: $sampleLog) {
      parser
      totals {
        add
        change
        destroy
      }
      metrics {
        key
        value
      }
    }
  }
`;

export const PREVIEW_DRAFT_PARSER_FOR_JOB = gql`
  mutation PreviewDraftParserForJob(
    $projectPath: String!
    $jobName: String!
    $specYaml: String!
    $limit: Int
    $ref: String
  ) {
    previewDraftParserForJob(
      projectPath: $projectPath
      jobName: $jobName
      specYaml: $specYaml
      limit: $limit
      ref: $ref
    ) {
      pipelineId
      status
      ref
      sha
      createdAt
      jobName
      variantCanonical
      variantSource
      variantReason
      variantRaw
      baselinePartitionKey
      jobType
      jobId
      jobStatus
      jobStage
      usedParser
      parserSource
      parserDiagnostics {
        reason
        expectedParser
        selectedParser
        source
        topCandidates {
          parser
          score
          matchedSignals
          expectedSignals
          matchedKeys
          matchedTotals
          matchedActions
        }
      }
      totals {
        add
        change
        destroy
      }
      byGroup {
        group
        add
        change
        destroy
        items {
          resource
          action
        }
      }
      metrics {
        key
        value
      }
      columns {
        key
        label
        value
        tooltip {
          title
          items {
            label
            value
          }
        }
      }
    }
  }
`;

export const UPSERT_CUSTOM_PARSER = gql`
  mutation UpsertCustomParser(
    $projectPath: String!
    $name: String!
    $specYaml: String!
    $jobName: String
  ) {
    upsertCustomParser(
      projectPath: $projectPath
      name: $name
      specYaml: $specYaml
      jobName: $jobName
    ) {
      name
      projectPath
      specYaml
      updatedAt
    }
  }
`;

export const DELETE_CUSTOM_PARSER = gql`
  mutation DeleteCustomParser($projectPath: String!, $name: String!) {
    deleteCustomParser(projectPath: $projectPath, name: $name)
  }
`;

export const COMPARE_PREFERENCES = gql`
  query ComparePreferences($projectPath: String!) {
    comparePreferences(projectPath: $projectPath) {
      baselineRef
      scopeRef
      baselineWindowSize
      scope
      lastSettledAnalysisTarget
    }
  }
`;

// Early-access usage tracking — exact monthly analysis units. Null for demo/fixtures.
export const WORKSPACE_USAGE_STATUS = gql`
  query WorkspaceUsageStatus($projectPath: String!) {
    workspaceUsageStatus(projectPath: $projectPath) {
      workspaceKey
      plan
      periodKey
      analysisUnitsUsed
      analysisUnitsLimit
      analysisUnitsRemaining
      isOverSoftLimit
      resetsAt
    }
  }
`;

export const SET_COMPARE_PREFERENCES = gql`
  mutation SetComparePreferences(
    $projectPath: String!
    $baselineRef: String
    $scopeRef: String
    $baselineWindowSize: Int
    $scope: String
  ) {
    setComparePreferences(
      projectPath: $projectPath
      baselineRef: $baselineRef
      scopeRef: $scopeRef
      baselineWindowSize: $baselineWindowSize
      scope: $scope
    ) {
      baselineRef
      scopeRef
      baselineWindowSize
      scope
    }
  }
`;

export const SET_LAST_SETTLED_ANALYSIS_TARGET = gql`
  mutation SetLastSettledAnalysisTarget($projectPath: String!, $target: String!) {
    setLastSettledAnalysisTarget(projectPath: $projectPath, target: $target)
  }
`;

export const COMPARE_CHUNK_FIELDS = gql`
  fragment CompareChunkFields on CompareChunk {
    sequence
    materializationStatus
    lifecycle {
      phase
      attemptComplete
      terminal
      resumable
      disposition
      ceilingHit
    }
    variants {
      family
      variant
      comparisonMode
      comparisonState
      stateDescription
      baselineContext
      scoring {
        rankingEligible
        zScore
        anomalyScore
        confidence
        outlier
        primaryRiskLabel
        riskFlags
        reasons
      }
      subjectWeightedScore
      baselineStats {
        count
        mean
        stddev
        p90
      }
      referenceQuality {
        sampleSize
        recency
        matchQuality
        parserConsistency
        overall
      }
      subject {
        jobId
        jobName
        jobStatus
        pipelineId
        addCount
        changeCount
        destroyCount
        replaceCount
        parseState
        family
        variant
        createdAt
        finishedAt
        sha
        byGroup {
          group
          add
          change
          destroy
          items {
            resource
            action
          }
        }
        metrics {
          key
          value
        }
      }
      baselines {
        jobId
        jobName
        jobStatus
        pipelineId
        branch
        sha
        addCount
        changeCount
        destroyCount
        replaceCount
        createdAt
        finishedAt
        byGroup {
          group
          add
          change
          destroy
          items {
            resource
            action
          }
        }
        metrics {
          key
          value
        }
      }
      baselineCount
      baselineTarget
      baselineBranch
      subjectPipelineId
    }
    progress {
      sequence
      phase
      label
      detail
      batchIndex
      pipelinesCrawled
      variantsTotal
      variantsReady
      variantsWorking
      baselinesFound
      baselineTarget
      final
      pipelinesInspected
      pipelinesTotal
      recommendedJobName
    }
    stats {
      variantsTotal
      variantsWithBaseline
      variantsWithSubject
      crawledPipelines
      parsedJobs
      progressState
      progress {
        phase
        batchesCompleted
        batchesTotal
        currentBatchCrawledPipelines
        currentBatchParsedJobs
        unitsCompleted
        unitsTotal
        percent
        label
      }
      comparableCount
      failedSubjectCount
      missingSubjectCount
      insufficientBaselineCount
      exhaustedBaselineCount
      partialSubjectCount
    }
  }
`;

const COMPARE_RECOMMENDATION_FIELDS = gql`
  fragment CompareRecommendationFields on SourceRecommendation {
    family
    rank
    confidence
    reasons
    recommendedParser
  }
`;

const COMPARE_PROGRESS_FIELDS = gql`
  ${COMPARE_CHUNK_FIELDS}
  ${COMPARE_RECOMMENDATION_FIELDS}
  fragment CompareProgressFields on CompareProgressResult {
    chunks @stream(initialCount: 1) {
      ...CompareChunkFields
    }
    recommendations {
      ...CompareRecommendationFields
    }
  }
`;

const COMPLETE_COMPARE_PROGRESS_FIELDS = gql`
  ${COMPARE_CHUNK_FIELDS}
  ${COMPARE_RECOMMENDATION_FIELDS}
  fragment CompleteCompareProgressFields on CompareProgressResult {
    chunks {
      ...CompareChunkFields
    }
    recommendations {
      ...CompareRecommendationFields
    }
  }
`;

export const COMPARE_JOBS_PROGRESS_COMPLETE = gql`
  ${COMPLETE_COMPARE_PROGRESS_FIELDS}
  query CompareJobsProgressComplete($input: CompareQueryInput!) {
    compareJobsProgress(input: $input) {
      ...CompleteCompareProgressFields
    }
  }
`;

export const COMPARE_JOBS_PROGRESS = gql`
  ${COMPARE_PROGRESS_FIELDS}
  query CompareJobsProgress($input: CompareQueryInput!) {
    compareJobsProgress(input: $input) {
      ...CompareProgressFields
    }
  }
`;

export const VIEW_BOOTSTRAP = gql`
  ${COMPARE_CHUNK_FIELDS}
  query ViewBootstrap($input: ViewBootstrapInput!) {
    viewBootstrap(input: $input) {
      bootGeneration
      viewKey
      readContextKey
      lastSettledAnalysisTarget
      connection {
        connectionId
        provider
        instanceUrl
      }
      projectMemory {
        provider
        connectionId
        lastProjectPath
        recentProjects {
          projectPath
          displayName
          selectedAt
          lastComparedAt
        }
      }
      project {
        state
        project {
          id
          pathWithNamespace
          webUrl
          defaultBranch
          provider
        }
      }
      catalog {
        state
        subjectPipeline {
          id
          status
          createdAt
          ref
          webUrl
        }
        groups {
          baseName
          label
          stage
          variants {
            key
            label
            jobName
            status
          }
        }
      }
      detector {
        state
        subjectPipeline {
          id
          status
          createdAt
          ref
          webUrl
        }
        rankedCandidates {
          jobName
          jobId
          pipelineId
          stage
          status
          score
          matchedSignals
          expectedSignals
        }
        recommendedJobName
        analyzedAt
      }
      comparison {
        state
        materializationStatus
        chunk {
          ...CompareChunkFields
        }
        recommendations {
          family
          rank
          confidence
          reasons
          recommendedParser
        }
      }
      approval {
        state
        windowHighWater
      }
    }
  }
`;

export const ANALYSIS_APPROVAL = gql`
  query AnalysisApproval($input: CompareQueryInput!) {
    analysisApproval(input: $input) {
      state
      windowHighWater
    }
  }
`;

export const PROJECT_ANALYSIS_STATUS = gql`
  query ProjectAnalysisStatus($projectPath: String!) {
    projectAnalysisStatus(projectPath: $projectPath) {
      state
      canManage
      updatedAt
    }
  }
`;

export const DISABLE_PROJECT_ANALYSIS = gql`
  mutation DisableProjectAnalysis($projectPath: String!) {
    disableProjectAnalysis(projectPath: $projectPath) {
      state
      canManage
      updatedAt
    }
  }
`;

export const ENABLE_PROJECT_ANALYSIS = gql`
  mutation EnableProjectAnalysis($projectPath: String!) {
    enableProjectAnalysis(projectPath: $projectPath) {
      state
      canManage
      updatedAt
    }
  }
`;

export const AUTHORIZE_AND_RUN_ANALYSIS = gql`
  ${COMPARE_PROGRESS_FIELDS}
  mutation AuthorizeAndRunAnalysis($input: CompareQueryInput!) {
    authorizeAndRunAnalysis(input: $input) {
      ...CompareProgressFields
    }
  }
`;

export const MIGRATE_ANALYSIS_APPROVALS = gql`
  mutation MigrateAnalysisApprovals($records: [LegacyAnalysisApprovalInput!]!) {
    migrateAnalysisApprovals(records: $records) {
      imported
      discarded
    }
  }
`;

export const BRANCH_PIPELINES = gql`
  query BranchPipelines(
    $projectPath: String!
    $branch: String!
    $page: Int
    $perPage: Int
    $allRefs: Boolean
  ) {
    branchPipelines(
      projectPath: $projectPath
      branch: $branch
      page: $page
      perPage: $perPage
      allRefs: $allRefs
    ) {
      id
      status
      ref
      sha
      createdAt
      families
      viewableFamilies
      workflowName
    }
  }
`;

export const BRANCH_PIPELINE_HISTORY = gql`
  query BranchPipelineHistory(
    $projectPath: String!
    $branch: String!
    $page: Int
    $perPage: Int
    $allRefs: Boolean
  ) {
    branchPipelineHistory(
      projectPath: $projectPath
      branch: $branch
      page: $page
      perPage: $perPage
      allRefs: $allRefs
    ) {
      freshness
      complete
      lastProviderCheckAt
      pipelines {
        id
        status
        ref
        sha
        createdAt
        families
        viewableFamilies
        workflowName
      }
    }
  }
`;

export const PIPELINE_FAMILY_STATUSES = gql`
  query PipelineFamilyStatuses($projectPath: String!, $pipelineIds: [ID!]!, $family: String!) {
    pipelineFamilyStatuses(projectPath: $projectPath, pipelineIds: $pipelineIds, family: $family) {
      pipelineId
      status
    }
  }
`;

export const DDB_CACHE_DEBUG = gql`
  query DdbCacheDebug($input: DdbCacheDebugInput!) {
    ddbCacheDebug(input: $input) {
      storageKey
      subjectPipeline {
        pipelineId
        branch
        sha
        status
        createdAt
        finishedAt
        jobsCrawledAt
        jobMetadataComplete
      }
      subjectPipelineSource
      subjectJobs {
        totalInDdb
        breakdown {
          discovered
          traceStored
          parsedOk
          parseFailed
        }
      }
      baselinesByVariant {
        family
        variant
        subjectJob {
          jobId
          pipelineId
          jobName
          jobStatus
          parseState
          stage
          family
          variant
          finishedAt
          createdAt
          sha
        }
        subjectParseState
        cachedBaselines
        requiredBaselines
        baselinesSufficient
        fastPathEligible
        baselinePipelineIds
        duplicateBaselineCandidateCount
        selectedBaselinesNewestAt
        selectedBaselinesOldestAt
        baselineCoverageStatus
        baselines {
          jobId
          pipelineId
          jobName
          jobStatus
          parseState
          stage
          finishedAt
          createdAt
        }
        unparsedBaselineCount
        baselineBranch
      }
      branchSearchBoundary {
        beforePipelineId
        oldestPipelineIdSeen
        oldestTimestampSeen
        stopReason
        successfulPipelineCount
        totalPipelinesSeen
        searchedAt
      }
      cachedPipelineCount
      anchor {
        type
        value
      }
      inspectedAt
    }
  }
`;

export const COMPARE_PERFORMANCE_DEBUG = gql`
  query ComparePerformanceDebug($input: ComparePerformanceDebugInput!) {
    comparePerformanceDebug(input: $input) {
      storageKey
      contextKey
      inspectedAt
      summary {
        attemptCount
        averageDurationMs
        p50DurationMs
        p95DurationMs
        maxDurationMs
        averageGapMs
        overlapCount
        budgetExhaustedCount
        slowRequestCount
        lastProgressState
        servingPaths {
          classifiedCount
          unclassifiedCount
          snapshotCount
          snapshotRate
          cacheAssemblyCount
          cacheAssemblyRate
          authorizedCount
          authorizedRate
          continuationCount
          continuationRate
        }
      }
      outboxHealth {
        available
        pendingCount
        failedCount
        retriedCount
        agedCount
        oldestAgeSeconds
        truncated
        inspectedAt
      }
      recommendations {
        severity
        code
        message
      }
      attempts {
        requestId
        servedFrom
        contextKey
        recordedAt
        startedAt
        finishedAt
        totalMs
        resolveSubjectMs
        variantProcessingMs
        variantsTotal
        variantsFastPath
        variantsSlowPath
        variantsCrawlBack
        variantsNoOpSlowPath
        budgetParsesUsed
        budgetParsesMax
        budgetCrawlsUsed
        budgetCrawlsMax
        budgetExhausted
        gitlabApiCalls
        tracesFetched
        jobsParsed
        pipelinesCrawled
        metadataCrawlMs
        metadataCrawlCount
        traceFetchMs
        traceFetchCount
        parseMs
        parseCount
        crawlMemoHits
        crawlMemoMisses
        parseMemoHits
        parseMemoMisses
        readMs
        fulfillmentMs
        timeToSnapshotMs
        progressState
        reason
        overlappedPrevious
        gapFromPreviousMs
        chunkCount
      }
    }
  }
`;

export const DDB_RECENT_PIPELINE_PROBE = gql`
  query DdbRecentPipelineProbe($projectPath: String!, $branch: String!, $limit: Int) {
    ddbRecentPipelineProbe(projectPath: $projectPath, branch: $branch, limit: $limit) {
      projectPath
      branch
      pipelines {
        pipelineId
        branch
        sha
        status
        source
        createdAt
        finishedAt
        jobMetadataComplete
      }
      totalInDdb
      inspectAt
    }
  }
`;

export const PROVIDER_PIPELINE_PROBE = gql`
  query ProviderPipelineProbe($projectPath: String!, $pipelineIds: [ID!]!) {
    providerPipelineProbe(projectPath: $projectPath, pipelineIds: $pipelineIds) {
      projectPath
      providerProjectPath
      pipelines {
        pipelineId
        found
        branch
        sha
        status
        source
        createdAt
        finishedAt
        error
      }
      inspectAt
    }
  }
`;

export const DDB_JOB_PROBE = gql`
  query DdbJobProbe($projectPath: String!, $branch: String!, $pipelineCount: Int) {
    ddbJobProbe(projectPath: $projectPath, branch: $branch, pipelineCount: $pipelineCount) {
      projectPath
      branch
      pipelinesInspected
      jobCount
      jobs {
        jobId
        pipelineId
        jobName
        jobStatus
        parseState
        family
        variant
        finishedAt
        gsi1pk
        gsi1sk
      }
      inspectAt
    }
  }
`;

export const SUBJECT_JOB_CATALOG = gql`
  query SubjectJobCatalog($input: SubjectJobCatalogInput!) {
    subjectJobCatalog(input: $input) {
      subjectPipeline {
        id
        status
        createdAt
        ref
        webUrl
      }
      groups {
        baseName
        label
        stage
        variants {
          key
          label
          jobName
          status
        }
      }
    }
  }
`;

export const SUBJECT_TERRAFORM_DETECTOR = gql`
  query SubjectTerraformDetector($input: SubjectTerraformDetectorInput!) {
    subjectTerraformDetector(input: $input) {
      subjectPipeline {
        id
        status
        createdAt
        ref
        webUrl
      }
      progressEvents {
        sequence
        label
        jobsInspected
        jobsTotal
        recommendedJobName
        final
      }
      rankedCandidates {
        jobName
        jobId
        pipelineId
        stage
        status
        score
        matchedSignals
        expectedSignals
      }
      recommendedJobName
    }
  }
`;

export const DEMO_JOB_TRACE = gql`
  query DemoJobTrace($projectPath: String!, $jobId: ID!) {
    demoJobTrace(projectPath: $projectPath, jobId: $jobId)
  }
`;
